/**
 * Adzuki. My Settings - opening from a redirected route
 *
 * SET-10, S43. The old settings routes no longer render the screens they replaced:
 * CpcentralApp::onStartWeb() redirects each one to the same host's index with the panel in the
 * fragment, and this is what reads it.
 *
 * WHY A FRAGMENT AND NOT A PATH OR A QUERY STRING. The server must not see which panel is open,
 * or S49 stops holding - that ruling says a settings shortcut no longer highlights in the shell
 * nav, and it rests on $active_link == $request['uri'] never matching. A query string is not
 * available either: hardcore.php:299 reads url segment 2 as the action name, so ?panel=x asks
 * for an action literally called that.
 *
 * THE FRAGMENT IS UNTRUSTED INPUT. Anyone can type one. It is matched against a strict pattern
 * and the action is rebuilt from the captured word rather than the string being passed through -
 * open() fetches whatever path it is handed, so "#//example.com/x" or a traversal would otherwise
 * be a request this page makes on someone else's behalf.
 */

(function MySettingsRoute($, Adzuki)
{
	"use strict";

	// An action, and SINCE SET-171 up to two further segments for a sub-screen's parameters -
	// a record id, and optionally one more word. #/MySettings/w9ChangeAddress/128 and
	// #/MySettings/w9ChangeName/128/legal are the shapes that exist today.
	//
	// TWO, NOT ANY NUMBER. The deepest action on the surface takes an id and a field type, so a
	// third segment is not something this application can serve - and a bound keeps the thing an
	// attacker can hand us small. Letters and digits only, so no dot, no encoded slash and no
	// traversal, and each segment is length-capped so a fragment cannot be used to build an
	// enormous request path.
	// SET-176: THE PANEL IS OPTIONAL, THE SLASH IN FRONT OF IT IS NOT.
	//
	// A bare #/MySettings named no panel, so it matched nothing, _panel() returned null and the
	// modal never opened - the page rendered and nothing reported anything, because an unmatched
	// fragment is the ordinary case on every other page in the application. QA asked whether that
	// was intended; it was not.
	//
	// THE SLASH AND THE PANEL MOVE TOGETHER, inside one optional group, which is what keeps the
	// sub-screen segments conditional on a panel being in front of them. #/MySettings//128 still
	// matches nothing, and so does a bare trailing slash - the group needs a letter after its
	// slash, so #/MySettings/ cannot satisfy it and cannot be consumed by what follows either.
	// AND THE PANEL IS LENGTH-CAPPED, WHICH IT NEVER WAS. The header above says every segment is
	// capped "so a fragment cannot be used to build an enormous request path", and that was true
	// of the sub-screen segments and not of the panel - [A-Za-z0-9]* is unbounded, so a fragment
	// could name a panel of any length at all. Found driving this file in node on 16 Oct 2026,
	// checking a bound the comment already claimed. 64 is the cap the other segments carry, and
	// the longest action on the surface is nowhere near it.
	var PANEL   = /^#\/MySettings(?:\/([A-Za-z][A-Za-z0-9]{0,63})((?:\/[A-Za-z0-9]{1,64}){0,2}))?$/;
	var SEGMENT = /^[A-Za-z0-9]{1,64}$/;

	// SET-165: WHERE A PANEL THAT CANNOT BE FETCHED LANDS INSTEAD.
	//
	// The pattern above proves the fragment is SHAPED like a panel, not that the panel
	// EXISTS - and the two are different questions. A panel can be retired (this ticket
	// retired Cruise Planners Mobile), renamed, or simply typed wrong, and until now every
	// one of those fetched a missing action and painted "Your settings could not be loaded"
	// over the modal. That was already true of any typed fragment; retiring a panel is just
	// the first time it had a way of happening to somebody who did nothing wrong.
	//
	// CHECKED BY FETCHING RATHER THAN BY A LIST HELD HERE. The nav is not on the page yet -
	// this runs at document ready, and the search index is fetched once the modal is already
	// open - so there is nothing to validate against without a second request. Letting the
	// fetch answer it costs nothing on the common path, where the panel is real.
	var DEFAULT_PANEL = 'myProfile';

	var _panel, _clearHash, _openPanel, _closeBound = false;

	_panel = function panel()
	{
		var match = PANEL.exec(window.location.hash || ''),
		    parts,
		    path,
		    i;

		if (!match) {
			return null;
		}

		// SET-176: NO PANEL NAMED MEANS THE DEFAULT, not nothing. This is the same answer the
		// surface already gives a fragment that names a panel it cannot fetch (SET-165 below) -
		// a fragment naming none is that question with less typing, so it gets that answer rather
		// than a second one.
		if (!match[1]) {
			return DEFAULT_PANEL;
		}

		// REBUILT FROM THE CAPTURED PIECES, NEVER PASSED THROUGH - the rule the header states, and
		// it matters more now there is more than one piece. open() fetches whatever path it is
		// handed, so every segment is re-tested on its own before it is appended rather than
		// trusting that the whole-string match implies each part is safe.
		path = match[1];

		if (match[2]) {
			parts = match[2].split('/');

			for (i = 0; i < parts.length; i++) {
				// split() on a string that begins with the separator yields a leading empty
				// member; it is not a segment and must not become an empty path element
				if (parts[i] === '') {
					continue;
				}

				if (!SEGMENT.test(parts[i])) {
					return null;
				}

				path += '/' + parts[i];
			}
		}

		return path;
	};

	/**
	 * Drops the fragment once the advisor closes the modal.
	 *
	 * Without this the panel stays in the address bar, and S37's reload-on-close - which fires
	 * when a save changed something the shell renders - would bring the advisor straight back
	 * into the modal they had just dismissed.
	 *
	 * replaceState rather than assigning location.hash, so closing the modal does not leave a
	 * history entry that Back would land on.
	 */
	_clearHash = function clearHash()
	{
		if (!window.history || typeof window.history.replaceState !== 'function') {
			return;
		}

		window.history.replaceState(
			null,
			'',
			window.location.pathname + window.location.search
		);
	};

	/**
	 * Opens the panel a fragment names, and arms the clean-up that removes it on close.
	 *
	 * THE CLOSE HANDLER IS BOUND HERE RATHER THAN AT READY, and only once. _clearHash() strips
	 * the WHOLE fragment, so binding it on a page that never had a settings fragment would let
	 * opening and closing the modal destroy a fragment belonging to something else.
	 */
	_openPanel = function openPanel(panel)
	{
		if (!_closeBound) {
			$(document).on('my-settings:closed', _clearHash);
			_closeBound = true;
		}

		Adzuki.MySettings.open(
			'/MySettings/' + panel,
			null,
			'/MySettings/' + DEFAULT_PANEL
		);
	};

	$(document).ready(function documentReady() {
		var panel = _panel();

		if (!Adzuki.MySettings) {
			return;
		}

		if (panel !== null) {
			_openPanel(panel);
		}

		// SET-171: A DEEP LINK FROM A PAGE THAT IS ALREADY THE TARGET PAGE CHANGES ONLY THE
		// FRAGMENT, so document ready never fires again and without this the link does nothing.
		// The CP Maxx dashboard is exactly that case - it IS the index the fragment points at, so
		// its own W9 prompt would have been the one link that silently failed.
		//
		// SAFE BECAUSE NOTHING ELSE WRITES THE HASH. The modal does not touch it while navigating
		// panels, and _clearHash() uses replaceState, which does not raise hashchange - so this
		// cannot loop by reacting to its own clean-up.
		$(window).on('hashchange', function hashChanged() {
			var next = _panel();

			if (next !== null) {
				_openPanel(next);
			}
		});
	});
}(jQuery, window.Adzuki || {}));
